import ExifReader from 'exifreader';
import imageCompression from 'browser-image-compression';
import { getCurrentCoords, embedGpsExif } from './gpsFallbackUtils';

/**
 * Validate/prepare a photo for upload and return its GPS coordinates.
 *
 * With `fallbackToDeviceLocation`, a photo that carries no usable GPS EXIF is
 * geo-tagged with the device's current location instead of being rejected —
 * the same behaviour the event upload flow uses. Callers that must insist on a
 * camera-tagged photo leave the option off and keep getting an error.
 */
export async function processGeotaggedImage(file, MAX_MB = 1, options = {}) {
  const { fallbackToDeviceLocation = false, section = 'RASActivity' } = options;
  const allowedExtensions = ['png', 'jpeg', 'jpg'];
  const ext = file.type.split('/')[1];
  if (!allowedExtensions.includes(ext)) {
    throw new Error('Only PNG, JPEG and JPG files are allowed');
  }

  let tags = {};
  try {
    tags = ExifReader.load(await file.arrayBuffer());
  } catch (exifErr) {
    // No readable metadata at all (screenshot, re-encoded copy, plain PNG).
    if (!fallbackToDeviceLocation) throw exifErr;
    console.warn('Failed to read EXIF:', exifErr);
  }

  const hasGpsTags =
    tags.GPSLatitude && tags.GPSLongitude && tags.GPSLatitude.value && tags.GPSLongitude.value;

  let latitude = null;
  let longitude = null;
  if (hasGpsTags) {
    const latRef = tags.GPSLatitudeRef ? tags.GPSLatitudeRef.value[0] : 'N';
    const lngRef = tags.GPSLongitudeRef ? tags.GPSLongitudeRef.value[0] : 'E';
    latitude = parseGPS(tags.GPSLatitude.value, tags.GPSLatitude.description, latRef);
    longitude = parseGPS(tags.GPSLongitude.value, tags.GPSLongitude.description, lngRef);
  }

  let workingFile = file;
  let geoTagAdded = false;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    if (!fallbackToDeviceLocation) {
      if (!hasGpsTags) {
        throw new Error('This image does not contain GPS location data. Please upload a photo taken with location/GPS enabled.');
      }
      throw new Error('Failed to extract valid GPS coordinates from image. Please ensure the photo is geo-tagged.');
    }
    // Rejects with `reason` set — the caller turns that into an actionable
    // message via getGeoFailureMessage().
    const coords = await getCurrentCoords();
    latitude = coords.latitude;
    longitude = coords.longitude;
    try {
      workingFile = await embedGpsExif(file, latitude, longitude);
    } catch (embedErr) {
      // EXIF write failed — still upload with the captured coordinates, which
      // is what the rest of the flow stores and displays.
      console.warn('Could not write GPS EXIF into the image:', embedErr);
    }
    geoTagAdded = true;
  }

  let processedFile = workingFile;
  const now = new Date();
  now.setHours(now.getHours() + 5);
  now.setMinutes(now.getMinutes() + 30);
  const istTimestamp = now.toISOString().replace(/:/g, '-');
  const dateStr = istTimestamp;
  // workingFile may be a PNG re-encoded as JPEG, so take the extension from it
  // rather than from the original file.
  const outExt = workingFile.type.split('/')[1] || ext;
  const newFileName = `${file.name}_${istTimestamp}_${section}.${outExt}`;
  if (workingFile.size > MAX_MB * 1024 * 1024) {
    const compressedBlob = await imageCompression(workingFile, {
      maxSizeMB: MAX_MB,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    });
    processedFile = new File([compressedBlob], newFileName, { type: workingFile.type });
  } else {
    processedFile = new File([workingFile], newFileName, { type: workingFile.type });
  }
  return { processedFile, latitude, longitude, timestamp: dateStr, geoTagAdded };
}

function parseGPS(gpsArr, description, ref) {
  // If description is already a valid number, use it
  if (description !== undefined && !isNaN(parseFloat(description))) {
    let val = parseFloat(description);
    if (ref === 'S' || ref === 'W') val = -val;
    return val;
  }

  if (!Array.isArray(gpsArr) || gpsArr.length !== 3) return null;
  
  const getVal = (item) => {
    if (Array.isArray(item) && item.length === 2) {
      if (item[1] === 0) return 0;
      return item[0] / item[1];
    }
    return Number(item) || 0;
  };

  const deg = getVal(gpsArr[0]);
  const min = getVal(gpsArr[1]);
  const sec = getVal(gpsArr[2]);
  
  if (isNaN(deg) || isNaN(min) || isNaN(sec)) return null;

  let decimal = deg + min / 60 + sec / 3600;
  if (ref === 'S' || ref === 'W') {
    decimal = -decimal;
  }
  return decimal;
}
